import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Select,
  Badge,
  Row,
  Col,
  Dropdown,
  message,
  Drawer,
  Form,
  Input,
  Menu,
  Modal,
} from "antd";
import {
  UserOutlined,
  ReloadOutlined,
  MoreOutlined,
  FileProtectOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  FilterOutlined,
  SearchOutlined,
  PhoneOutlined,
  IdcardOutlined,
  MailOutlined,
  SafetyOutlined,
  TransactionOutlined,
  EditOutlined,
  StopOutlined,
  KeyOutlined,
  // TeamOutlined,
} from "@ant-design/icons";
import { formatDate } from "../../utils/format";
import UserProfileDrawer from "./UserProfileDrawer";
import ComplianceDrawer from "./ComplianceDrawer";
import { api } from "../../services/authService";
import {
  getAdminApiKeys,
  normalizeAdminApiKeys,
  revokeAdminApiKey,
} from "../../services/apiKeyService";
import { useNavigate } from "react-router-dom";
import UserTransactionsModal from "../transactions/UserTransactionsModal";
import UserStatusBadge from "./UserStatusBadge";
// import UserEmployeesModal from "./UserEmployeesModal";

const { Text, Title } = Typography;
const { Option } = Select;

const BusinessUsers = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState({});
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [complianceDrawerVisible, setComplianceDrawerVisible] = useState(false);
  const [transactionsModalVisible, setTransactionsModalVisible] =
    useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const [emailEditModalVisible, setEmailEditModalVisible] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [apiKeyRevokeModalVisible, setApiKeyRevokeModalVisible] =
    useState(false);
  const [revokingApiKey, setRevokingApiKey] = useState(false);
  const [businessForApiKeyRevoke, setBusinessForApiKeyRevoke] = useState(null);
  const [adminApiKeys, setAdminApiKeys] = useState([]);
  const [emailForm] = Form.useForm();
  const [apiKeyRevokeForm] = Form.useForm();
  // const [employeesModalVisible, setEmployeesModalVisible] = useState(false);
  // const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [form] = Form.useForm();

  const observerTarget = useRef(null);
  const firstMount = useRef(true);
  const LIMIT = 15;
  const navigate = useNavigate();
  const filtersRef = useRef(filters);

  const fetchAdminApiKeys = useCallback(async () => {
    try {
      const data = await getAdminApiKeys();
      setAdminApiKeys(normalizeAdminApiKeys(data));
    } catch (error) {
      console.error("Error fetching admin API keys for businesses:", error);
    }
  }, []);

  // Check if there are any active filters
  const hasActiveFilters = useMemo(() => {
    const activeFilterCount = Object.keys(filters).filter(
      (key) =>
        filters[key] !== undefined &&
        filters[key] !== null &&
        filters[key] !== "" &&
        !(Array.isArray(filters[key]) && filters[key].length === 0)
    ).length;
    return activeFilterCount > 0;
  }, [filters]);

  const fetchUsers = useCallback(
    async (reset = false) => {
      if (loading || (!hasMore && !reset)) return;

      try {
        setLoading(true);
        const params = new URLSearchParams();
        params.set("limit", LIMIT.toString());

        if (!reset && nextCursor) {
          params.set("next_cursor", nextCursor);
        }

        const currentFilters = filtersRef.current;

        // Add search parameters if they exist
        if (currentFilters.profile_id && currentFilters.profile_id.trim()) {
          params.set("id", currentFilters.profile_id.trim());
        }
        if (currentFilters.email && currentFilters.email.trim()) {
          params.set("email", currentFilters.email.trim());
        }

        // Add filter parameters if they exist
        if (currentFilters.status) {
          params.set("status", currentFilters.status);
        }

        // If any search parameter is present, don't use cursor-based pagination
        const isSearching = !!(
          currentFilters.profile_id ||
          currentFilters.email
        );

        // Remove next_cursor if we're searching
        if (isSearching && params.has("next_cursor")) {
          params.delete("next_cursor");
        }

        const response = await api.get(`/admin/businesses?${params}`);
        const data = response.data;

        // Handle special case for profile_id search (might return a single object)
        if (data.id) {
          setUsers([data]);
          setNextCursor(null);
          setHasMore(false);
          setTotal(1);
          setLoading(false);
          return;
        }

        const newUsers = data.users || [];

        // For profile ID search, only show the matching user
        if (currentFilters.profile_id && currentFilters.profile_id.trim()) {
          const matchingUser = newUsers.find(
            (user) => user.id === currentFilters.profile_id.trim()
          );
          setUsers(matchingUser ? [matchingUser] : []);
          setNextCursor(null);
          setHasMore(false);
          setTotal(matchingUser ? 1 : 0);
          setLoading(false);
          return;
        }

        // For regular list view
        setUsers(reset ? newUsers : [...users, ...newUsers]);
        setNextCursor(data.next_cursor);
        setHasMore(!!data.next_cursor);
        setTotal(
          data.total !== undefined
            ? data.total
            : reset
            ? newUsers.length
            : users.length + newUsers.length
        );
      } catch (error) {
        console.error("Error fetching business users:", error);
        message.error("Failed to fetch business users. Please try again.");
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [nextCursor, loading, hasMore, users]
  );

  // Handle filter submission
  const handleFilterSubmit = useCallback(
    (values) => {
      // Clean up the values by removing empty/undefined values
      const cleanValues = Object.entries(values).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== "" && value !== null) {
          acc[key] = value;
        }
        return acc;
      }, {});

      // Update both the state and ref simultaneously
      setFilters(cleanValues);
      filtersRef.current = cleanValues;

      setFilterVisible(false);
      // Reset pagination when applying new filters
      setNextCursor(null);
      setHasMore(true);
      fetchUsers(true);
    },
    [fetchUsers]
  );

  // Reset filters
  const handleResetFilters = useCallback(() => {
    form.resetFields();

    const emptyValues = {
      profile_id: undefined,
      email: undefined,
      status: undefined,
    };

    form.setFieldsValue(emptyValues);
  }, [form]);

  // Handle refresh button
  const handleRefresh = useCallback(() => {
    // Reset all state values
    setUsers([]); // Clear existing users
    setNextCursor(null); // Reset pagination cursor
    setHasMore(true); // Reset hasMore flag
    setFilters({}); // Clear filters
    filtersRef.current = {}; // Clear filters ref

    // Clear form fields
    form.resetFields();
    form.setFieldsValue({
      profile_id: undefined,
      email: undefined,
      status: undefined,
    });

    // Fetch users with reset flag
    fetchUsers(true);
    fetchAdminApiKeys();
  }, [fetchAdminApiKeys, fetchUsers, form]);

  // Update the ref whenever filters change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Initial load
  useEffect(() => {
    // Only fetch on first mount
    if (firstMount.current) {
      firstMount.current = false;
      fetchUsers(true);
      fetchAdminApiKeys();
    }
  }, [fetchAdminApiKeys]); // Only run on mount

  useEffect(() => {
    if (!observerTarget.current || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchUsers(false);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(observerTarget.current);

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
      observer.disconnect();
    };
  }, [hasMore, loading, fetchUsers]);

  const handleUserClick = async (user) => {
    try {
      const response = await api.get(
        `/admin/businesses/${user.id}?include_compliance=true`
      );
      const userData = response.data;

      // Log the data to debug
      console.log("DEBUG - Business user data from API:", userData);

      // Make sure we pass all required fields to the UserProfileDrawer
      setSelectedUser({
        ...userData, // Include all properties from the API response
        id: userData.id,
        email: userData.email,
        business_name: userData.business_name,
        phone_number: userData.phone_number,
        contact_name: userData.contact_name,
        contact_role: userData.contact_role,
        legal_structure: userData.legal_structure,
        industry: userData.industry,
        country: userData.country,
        status: userData.status,
        created: userData.created,
      });

      setDrawerVisible(true);
    } catch (error) {
      console.error("Error loading business details:", error);
      message.error("Failed to load business details. Please try again.");

      // If the API call fails, fallback to the data we already have
      if (user) {
        console.log("DEBUG - Using fallback business data:", user);

        setSelectedUser({
          ...user, // Include all properties we already have
          id: user.id,
          email: user.email || "N/A",
          business_name: user.business_name || "N/A",
          phone_number: user.phone_number || "N/A",
          contact_name: user.contact_name || "N/A",
          contact_role: user.contact_role || "N/A",
          legal_structure: user.legal_structure || "N/A",
          industry: user.industry || "N/A",
          country: user.country || "N/A",
          status: user.status || "N/A",
          created: user.created,
        });
        setDrawerVisible(true);
      }
    }
  };

  const handleComplianceClick = (user) => {
    setSelectedUser(user);
    setComplianceDrawerVisible(true);
  };

  const handleStatusChange = async (userId, status) => {
    if (currentUser?.role !== "SUPER_ADMIN") {
      message.error("Only SUPER_ADMIN users can modify business status");
      return;
    }

    try {
      await api.patch(`/admin/businesses/${userId}`, {
        action: "modify-status",
        status,
      });

      message.success(`Business status updated to ${status}`);
      fetchUsers(true);
    } catch (error) {
      console.error("Error updating business status:", error);
      message.error("Failed to update business status. Please try again.");
    }
  };

  const handleEditEmail = (business) => {
    setEditingBusiness(business);
    emailForm.setFieldsValue({
      email: business.email,
    });
    setEmailEditModalVisible(true);
  };

  const handleEmailUpdate = async (values) => {
    if (!editingBusiness) return;

    try {
      await api.patch(`/admin/businesses/${editingBusiness.id}`, {
        action: "modify-data",
        email: values.email,
      });

      message.success("Business email updated successfully");
      setEmailEditModalVisible(false);
      setEditingBusiness(null);
      emailForm.resetFields();
      fetchUsers(true); // Refresh the list
    } catch (error) {
      console.error("Error updating business email:", error);
      message.error("Failed to update business email. Please try again.");
    }
  };

  // Handle view transactions for a business
  const handleViewTransactions = (business) => {
    setSelectedUser(business);
    setTransactionsModalVisible(true);
  };

  const adminApiKeyByBusinessId = useMemo(() => {
    const keyByBusinessId = new Map();

    adminApiKeys.forEach((apiKey) => {
      const businessIds = [
        apiKey.profile_id,
        apiKey.profileId,
        apiKey.user_id,
        apiKey.userId,
        apiKey.business_id,
        apiKey.businessId,
        apiKey.profile?.id,
        apiKey.user?.id,
        apiKey.business?.id,
      ].filter(Boolean);

      businessIds.forEach((businessId) => {
        const currentApiKey = keyByBusinessId.get(businessId);

        if (!currentApiKey || apiKey.status === "active") {
          keyByBusinessId.set(businessId, apiKey);
        }
      });
    });

    return keyByBusinessId;
  }, [adminApiKeys]);

  const getBusinessApiKey = (business) => {
    if (!business) return null;

    if (business.api_key) return business.api_key;
    if (business.apiKey) return business.apiKey;
    if (Array.isArray(business.api_keys)) {
      return (
        business.api_keys.find((apiKey) => apiKey?.status === "active") ||
        business.api_keys[0] ||
        null
      );
    }
    if (Array.isArray(business.apiKeys)) {
      return (
        business.apiKeys.find((apiKey) => apiKey?.status === "active") ||
        business.apiKeys[0] ||
        null
      );
    }

    return (
      adminApiKeyByBusinessId.get(business.id) ||
      adminApiKeyByBusinessId.get(business.profile_id) ||
      adminApiKeyByBusinessId.get(business.profileId) ||
      adminApiKeyByBusinessId.get(business.user_id) ||
      adminApiKeyByBusinessId.get(business.userId) ||
      null
    );
  };

  const getApiKeyId = (business) => {
    const apiKey = getBusinessApiKey(business);
    return apiKey?.id || business?.api_key_id || business?.apiKeyId || null;
  };

  const getApiKeyStatus = (business) => {
    const apiKey = getBusinessApiKey(business);
    return apiKey?.status || business?.api_key_status || business?.apiKeyStatus;
  };

  const getApiKeyTagColor = (status) => {
    const colors = {
      active: "success",
      expired: "warning",
      revoked: "error",
    };

    return colors[status] || "default";
  };

  const handleOpenApiKeyRevokeModal = (business) => {
    const apiKeyId = getApiKeyId(business);

    if (!apiKeyId) {
      message.warning("No API key ID is attached to this business yet.");
      return;
    }

    setBusinessForApiKeyRevoke(business);
    apiKeyRevokeForm.setFieldsValue({ reason: "" });
    setApiKeyRevokeModalVisible(true);
  };

  const handleRevokeApiKey = async (values) => {
    const apiKeyId = getApiKeyId(businessForApiKeyRevoke);

    if (!apiKeyId) {
      message.error("No API key ID found for this business.");
      return;
    }

    try {
      setRevokingApiKey(true);
      await revokeAdminApiKey(apiKeyId, values.reason);

      message.success("API key revoked successfully");
      setApiKeyRevokeModalVisible(false);
      setBusinessForApiKeyRevoke(null);
      apiKeyRevokeForm.resetFields();
      fetchUsers(true);
    } catch (error) {
      console.error("Error revoking API key:", error);
      message.error("Failed to revoke API key. Please try again.");
    } finally {
      setRevokingApiKey(false);
    }
  };

  // const handleViewEmployees = (record) => {
  //   setSelectedEnterprise(record);
  //   setEmployeesModalVisible(true);
  // };

  const getActionMenu = (record) => {
    const items = [
      {
        key: "view",
        icon: <EyeOutlined />,
        label: "View Profile",
        onClick: () => handleUserClick(record),
      },
      {
        key: "compliance",
        icon: <FileProtectOutlined />,
        label: "View Compliance",
        onClick: () => handleComplianceClick(record),
      },
      {
        key: "transactions",
        icon: <TransactionOutlined />,
        label: "View Transactions",
        onClick: () => handleViewTransactions(record),
      },
      {
        key: "edit-email",
        icon: <EditOutlined />,
        label: "Edit Email",
        onClick: () => handleEditEmail(record),
      },
      // {
      //   key: "employees",
      //   icon: <TeamOutlined />,
      //   label: "View Employees",
      //   onClick: () => handleViewEmployees(record),
      // },
    ];

    if (currentUser?.role === "SUPER_ADMIN" && record.status === "pending") {
      items.push(
        {
          key: "approve",
          icon: <CheckOutlined style={{ color: "#52c41a" }} />,
          label: "Approve Business",
          onClick: () => handleStatusChange(record.id, "approved"),
        },
        {
          key: "decline",
          icon: <CloseOutlined style={{ color: "#ff4d4f" }} />,
          label: "Decline Business",
          onClick: () => handleStatusChange(record.id, "declined"),
        }
      );
    }

    const apiKeyId = getApiKeyId(record);
    const apiKeyStatus = getApiKeyStatus(record);

    if (apiKeyId && apiKeyStatus !== "revoked") {
      items.push({
        key: "revoke-api-key",
        icon: <StopOutlined style={{ color: "#ff4d4f" }} />,
        label: "Revoke API Key",
        onClick: () => handleOpenApiKeyRevokeModal(record),
      });
    }

    return items;
  };

  const columns = [
    {
      title: "Business Name",
      dataIndex: "business_name",
      key: "business_name",
      render: (name, record) => (
        <Space>
          <UserOutlined />
          {name || "N/A"}
        </Space>
      ),
    },
    {
      title: "Business Domain",
      dataIndex: "business_domain",
      key: "business_domain",
      render: (domain) => domain || "N/A",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => email || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <UserStatusBadge status={status} />,
    },
    {
      title: "API Key",
      key: "api_key",
      render: (_, record) => {
        const apiKeyStatus = getApiKeyStatus(record);

        if (!getApiKeyId(record)) {
          return (
            <Space>
              <KeyOutlined />
              <Text type="secondary">No key</Text>
            </Space>
          );
        }

        return (
          <Badge
            status={getApiKeyTagColor(apiKeyStatus)}
            text={apiKeyStatus?.toUpperCase() || "AVAILABLE"}
          />
        );
      },
    },
    {
      title: "Created",
      dataIndex: "created",
      key: "created",
      render: (timestamp) => formatDate(timestamp) || "N/A",
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 80,
      render: (_, record) => (
        <Dropdown
          menu={{ items: getActionMenu(record) }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  // New function to detect screen size
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    // Function to check if window width is below a threshold
    const checkWindowSize = () => {
      setIsSmallScreen(window.innerWidth < 1200);
    };

    // Initial check
    checkWindowSize();

    // Add event listener for window resize
    window.addEventListener("resize", checkWindowSize);

    // Clean up
    return () => window.removeEventListener("resize", checkWindowSize);
  }, []);

  return (
    <>
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              Business Customers
            </Title>
          </Space>
        }
        extra={
          <Space>
            <Badge dot={hasActiveFilters} offset={[-5, 5]} color="#1890ff">
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFilterVisible(true)}
                type={hasActiveFilters ? "primary" : "default"}
              >
                Filter{" "}
                {hasActiveFilters
                  ? `(${
                      Object.keys(filters).filter(
                        (key) =>
                          filters[key] !== undefined &&
                          filters[key] !== null &&
                          filters[key] !== "" &&
                          !(
                            Array.isArray(filters[key]) &&
                            filters[key].length === 0
                          )
                      ).length
                    })`
                  : ""}
              </Button>
            </Badge>
            <Button onClick={handleRefresh} loading={loading}>
              Refresh
            </Button>
          </Space>
        }
      >
        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={isSmallScreen ? { x: 1200 } : undefined}
          />
        </div>
        {hasMore && !loading && (
          <div
            ref={observerTarget}
            style={{ height: "20px", margin: "20px 0" }}
          />
        )}
      </Card>

      {/* Filter Drawer */}
      <Drawer
        title="Filter Business Customers"
        placement="right"
        onClose={() => setFilterVisible(false)}
        open={filterVisible}
        width={400}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFilterSubmit}
          initialValues={filters}
        >
          <Form.Item name="profile_id" label="Profile ID">
            <Input
              placeholder="Search by Profile ID"
              prefix={<IdcardOutlined style={{ color: "#1890ff" }} />}
              allowClear
            />
          </Form.Item>

          <Form.Item name="email" label="Email">
            <Input
              placeholder="Search by email"
              prefix={<MailOutlined style={{ color: "#1890ff" }} />}
              allowClear
            />
          </Form.Item>

          <Form.Item label="Status" name="status">
            <Select
              placeholder="Select status"
              allowClear
              style={{ width: "100%" }}
            >
              <Option value="pending">Pending</Option>
              <Option value="approved">Approved</Option>
              <Option value="declined">Declined</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={handleResetFilters}>Clear Selections</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<FilterOutlined />}
              >
                Apply Filters
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>

      {drawerVisible && (
        <UserProfileDrawer
          visible={drawerVisible}
          onClose={() => {
            setDrawerVisible(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          userType="business"
        />
      )}

      <ComplianceDrawer
        visible={complianceDrawerVisible}
        onClose={() => {
          setComplianceDrawerVisible(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        currentUser={currentUser}
      />

      <UserTransactionsModal
        visible={transactionsModalVisible}
        onClose={() => {
          setTransactionsModalVisible(false);
        }}
        profileId={selectedUser?.id}
        userName={selectedUser?.business_name}
      />

      {/* {selectedEnterprise && (
        <UserEmployeesModal
          visible={employeesModalVisible}
          onClose={() => {
            setEmployeesModalVisible(false);
            setSelectedEnterprise(null);
          }}
          enterprise={selectedEnterprise}
        />
      )} */}

      {/* Email Edit Modal */}
      <Modal
        title="Update Business Email"
        open={emailEditModalVisible}
        onCancel={() => {
          setEmailEditModalVisible(false);
          setEditingBusiness(null);
          emailForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={emailForm}
          layout="vertical"
          onFinish={handleEmailUpdate}
        >
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please enter email address' },
              { type: 'email', message: 'Please enter a valid email address' },
            ]}
          >
            <Input
              placeholder="Enter new email address"
              prefix={<MailOutlined style={{ color: '#1890ff' }} />}
            />
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setEmailEditModalVisible(false);
                  setEditingBusiness(null);
                  emailForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update Email
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Revoke API Key"
        open={apiKeyRevokeModalVisible}
        onCancel={() => {
          setApiKeyRevokeModalVisible(false);
          setBusinessForApiKeyRevoke(null);
          apiKeyRevokeForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={apiKeyRevokeForm}
          layout="vertical"
          onFinish={handleRevokeApiKey}
        >
          <Form.Item>
            <Text>
              This will revoke the API key for{" "}
              <strong>
                {businessForApiKeyRevoke?.business_name || "this business"}
              </strong>
              .
            </Text>
          </Form.Item>

          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: "Please enter a reason" }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Enter reason for revoking this API key"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button
                onClick={() => {
                  setApiKeyRevokeModalVisible(false);
                  setBusinessForApiKeyRevoke(null);
                  apiKeyRevokeForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button
                danger
                type="primary"
                htmlType="submit"
                loading={revokingApiKey}
              >
                Revoke API Key
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

BusinessUsers.propTypes = {
  currentUser: PropTypes.shape({
    id: PropTypes.string,
    role: PropTypes.string,
    email: PropTypes.string,
  }),
};

BusinessUsers.defaultProps = {
  currentUser: null,
};

export default BusinessUsers;
